import { CreditCardOutlined } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { PayPalButtons, PayPalScriptProvider, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, { useCallback, useEffect, useState } from "react";
import { gateway } from "../gateway";
import { getEnvVar } from "../utils/get-env";
import { colors } from "./ui/colors";

const stripePublishableKey = getEnvVar("REACT_APP_STRIPE_PUBLISHABLE_KEY") ?? "";
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

type UpdatePaygPaymentMethodProps = {
  subscriptionId: string;
  companyId?: number | string | null;
  planId?: number | string | null;
  currentMethodLabel?: string | null;
  onSuccess?: (result?: any) => void;
  onError?: (message: string) => void;
};

const PayPalVaultButtonsContainer: React.FC<any> = (props) => {
  const [{ isPending }] = usePayPalScriptReducer();

  if (isPending) {
    return (
      <Box sx={{ width: "100%", py: 1 }}>
        <Skeleton variant="rectangular" width="100%" height={48} sx={{ borderRadius: 2, bgcolor: "rgba(251, 142, 0, 0.12)" }} />
      </Box>
    );
  }

  return <PayPalButtons {...props} />;
};

const StripeReplaceCardForm: React.FC<{
  subscriptionId: string;
  tokenLocal: string | null;
  setErrorMessage: (msg: string) => void;
  onSuccess?: (result?: any) => void;
  onError?: (message: string) => void;
}> = ({ subscriptionId, tokenLocal, setErrorMessage, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitCard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      setErrorMessage("Stripe is not initialized yet. Please try again.");
      return;
    }

    setSubmitting(true);
    setErrorMessage("");

    try {
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });

      if (result.error) {
        const msg = result.error.message || "Failed to authorize payment method.";
        setErrorMessage(msg);
        onError?.(msg);
        setSubmitting(false);
        return;
      }

      const setupIntent = result.setupIntent;
      const paymentMethodId = typeof setupIntent?.payment_method === "string" ? setupIntent.payment_method : setupIntent?.payment_method?.id;

      if (!paymentMethodId || setupIntent?.status !== "succeeded") {
        const msg = "Setup Intent did not succeed. Please verify your card details.";
        setErrorMessage(msg);
        onError?.(msg);
        setSubmitting(false);
        return;
      }

      const response = await gateway.post(
        `/subscriptions/${subscriptionId}/billing-vault`,
        {
          provider: "stripe",
          paymentMethodId,
        },
        {
          headers: {
            Authorization: tokenLocal ? `Bearer ${tokenLocal}` : undefined,
          },
        },
      );

      onSuccess?.(response?.data);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Failed to update payment method with Stripe.";
      setErrorMessage(msg);
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmitCard} style={{ width: "100%" }}>
      <Stack spacing={2} sx={{ width: "100%" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
          Credit / Debit card (Stripe)
        </Typography>
        <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper" }}>
          <Stack spacing={2}>
            <PaymentElement
              options={{
                layout: "tabs",
                defaultValues: {
                  billingDetails: {
                    address: {
                      country: "US",
                    },
                  },
                },
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={!stripe || !elements || submitting}
              sx={{ py: 1.2, fontWeight: 700, borderRadius: 2 }}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : "Save new card"}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </form>
  );
};

const StripeReplaceSection: React.FC<{
  companyId: any;
  subscriptionId: string;
  tokenLocal: string | null;
  setErrorMessage: (msg: string) => void;
  onSuccess?: (result?: any) => void;
  onError?: (message: string) => void;
}> = (props) => {
  const [clientSecret, setClientSecret] = useState("");
  const [loadingSetup, setLoadingSetup] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const initSetupIntent = async () => {
      if (!props.companyId) {
        if (isMounted) setLoadingSetup(false);
        return;
      }
      try {
        if (isMounted) {
          setLoadingSetup(true);
          props.setErrorMessage("");
        }
        const response = await gateway.post("/stripe/vault/setup-intent", {
          companyId: Number(props.companyId),
        });
        const resData = response?.data?.data || response?.data || {};
        const secret = resData?.clientSecret || resData?.client_secret;
        if (!secret) throw new Error("No client secret returned from Stripe SetupIntent endpoint.");
        if (isMounted) setClientSecret(secret);
      } catch (err: any) {
        if (isMounted) {
          props.setErrorMessage(err?.response?.data?.message || err?.message || "Failed to initialize Stripe card checkout.");
        }
      } finally {
        if (isMounted) setLoadingSetup(false);
      }
    };

    initSetupIntent();
    return () => {
      isMounted = false;
    };
  }, [props.companyId]);

  if (!stripePromise) {
    return (
      <Alert severity="warning" sx={{ width: "100%" }}>
        Stripe publishable key is not configured.
      </Alert>
    );
  }

  if (loadingSetup) {
    return <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 2 }} />;
  }

  if (!clientSecret) return null;

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
      <StripeReplaceCardForm
        subscriptionId={props.subscriptionId}
        tokenLocal={props.tokenLocal}
        setErrorMessage={props.setErrorMessage}
        onSuccess={props.onSuccess}
        onError={props.onError}
      />
    </Elements>
  );
};

export const UpdatePaygPaymentMethod: React.FC<UpdatePaygPaymentMethodProps> = ({
  subscriptionId,
  companyId,
  planId,
  currentMethodLabel,
  onSuccess,
  onError,
}) => {
  const [selectedMethod, setSelectedMethod] = useState<"card" | "paypal">("card");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const paypalClientId = getEnvVar("REACT_APP_PAYPAL_MERCHANT_CLIENT_ID") || "";
  const paypalPartnerAttributionId = getEnvVar("REACT_APP_PAYPAL_PARTNER_ATTRIBUTION_ID") || "";
  const tokenLocal = sessionStorage.getItem("token");

  const handleReplaceWithPaypal = useCallback(
    async (setupTokenId: string) => {
      if (!setupTokenId) {
        setErrorMessage("Missing PayPal setup token.");
        return;
      }

      try {
        setLoading(true);
        setErrorMessage("");
        const response = await gateway.post(
          `/subscriptions/${subscriptionId}/billing-vault`,
          {
            provider: "paypal",
            setupTokenId,
          },
          {
            headers: {
              Authorization: tokenLocal ? `Bearer ${tokenLocal}` : undefined,
            },
          },
        );
        onSuccess?.(response?.data);
      } catch (error: any) {
        const msg = error?.response?.data?.message || error?.data?.message || error?.message || "Failed to update PayPal payment method.";
        setErrorMessage(msg);
        onError?.(msg);
      } finally {
        setLoading(false);
      }
    },
    [subscriptionId, tokenLocal, onSuccess, onError],
  );

  if (!paypalClientId) {
    return (
      <Alert severity="error" sx={{ width: "100%" }}>
        PayPal client ID is not set.
      </Alert>
    );
  }

  const initialOptions: any = {
    "client-id": paypalClientId,
    components: "buttons",
    "data-partner-attribution-id": paypalPartnerAttributionId,
    "data-page-type": "checkout",
    vault: "true",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      {currentMethodLabel ? (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Current method: {currentMethodLabel}
        </Typography>
      ) : null}
      <Typography variant="body2" color="text.secondary">
        Add a new payment method. The previous one will be replaced and any pending Flow Mobile balance will be charged with the new method.
      </Typography>

      {errorMessage && (
        <Alert severity="error" sx={{ width: "100%" }}>
          {errorMessage}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
          <CircularProgress size={28} />
          <Typography variant="body2">Updating payment method...</Typography>
        </Box>
      ) : (
        <Stack spacing={2} sx={{ width: "100%" }}>
          <Paper
            elevation={0}
            sx={{
              p: 0.5,
              bgcolor: "var(--fx-surface-muted, #F1F5F9)",
              borderRadius: 2.5,
              display: "flex",
              gap: 0.5,
            }}
          >
            <Button
              fullWidth
              onClick={() => setSelectedMethod("card")}
              startIcon={<CreditCardOutlined sx={{ fontSize: 18 }} />}
              sx={{
                py: 1,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: "0.82rem",
                textTransform: "none",
                bgcolor: selectedMethod === "card" ? "var(--fx-surface-base, #FFFFFF)" : "transparent",
                color: selectedMethod === "card" ? colors.orange.base : "#64748B",
                boxShadow: selectedMethod === "card" ? "0 2px 6px rgba(0,0,0,0.06)" : "none",
                "&:hover": {
                  bgcolor: selectedMethod === "card" ? "var(--fx-surface-base, #FFFFFF)" : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.03)",
                },
              }}
            >
              Credit / Debit Card
            </Button>
            <Button
              fullWidth
              onClick={() => setSelectedMethod("paypal")}
              startIcon={
                <Box
                  component="span"
                  sx={{
                    fontWeight: 900,
                    fontSize: "0.9rem",
                    fontStyle: "italic",
                    color: selectedMethod === "paypal" ? "#003087" : "var(--fx-surface-textsecondary, #64748B)",
                  }}
                >
                  P
                </Box>
              }
              sx={{
                py: 1,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: "0.82rem",
                textTransform: "none",
                bgcolor: selectedMethod === "paypal" ? "var(--fx-surface-base, #FFFFFF)" : "transparent",
                color: selectedMethod === "paypal" ? "#003087" : "var(--fx-surface-textsecondary, #64748B)",
                boxShadow: selectedMethod === "paypal" ? "0 2px 6px rgba(0,0,0,0.06)" : "none",
                "&:hover": {
                  bgcolor: selectedMethod === "paypal" ? "var(--fx-surface-base, #FFFFFF)" : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.03)",
                },
              }}
            >
              PayPal
            </Button>
          </Paper>

          {selectedMethod === "card" ? (
            <StripeReplaceSection
              key={`replace-stripe-${subscriptionId}-${companyId}`}
              companyId={companyId}
              subscriptionId={subscriptionId}
              tokenLocal={tokenLocal}
              setErrorMessage={setErrorMessage}
              onSuccess={onSuccess}
              onError={onError}
            />
          ) : (
            <Box sx={{ pt: 1 }}>
              <PayPalScriptProvider options={initialOptions}>
                <PayPalVaultButtonsContainer
                  key={`replace-paypal-${subscriptionId}`}
                  style={{ shape: "rect", layout: "vertical" }}
                  createVaultSetupToken={async () => {
                    try {
                      setErrorMessage("");
                      const response = await gateway.post("/paypal/vault/setup-tokens", {
                        ...(planId ? { planId } : {}),
                        paymentSource: "paypal",
                      });
                      const responseData = response?.data?.data || response?.data || {};
                      const tokenId = responseData?.setupTokenId || responseData?.id || responseData?.token;
                      if (!tokenId) {
                        throw new Error(responseData?.message || "No setup token returned from backend");
                      }
                      return tokenId;
                    } catch (err: any) {
                      setErrorMessage(err?.response?.data?.message || err?.message || "Could not initiate PayPal authorization.");
                      throw err;
                    }
                  }}
                  onApprove={async (data: any) => {
                    const tokenId = data?.vaultSetupToken || data?.setupTokenId || data?.approval_token_id;
                    if (tokenId) {
                      await handleReplaceWithPaypal(tokenId);
                    } else {
                      setErrorMessage("PayPal did not return a valid setup token ID.");
                    }
                  }}
                  onCancel={() => {
                    setLoading(false);
                    setErrorMessage("");
                  }}
                  onError={(err: any) => {
                    setLoading(false);
                    const msg = err?.message || "";
                    if (!msg.includes("postrobot_method") && !msg.includes("closed")) {
                      setErrorMessage(msg || "PayPal authorization failed.");
                    }
                  }}
                />
              </PayPalScriptProvider>
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
};
